import React from 'react';

import bluebird from 'bluebird';

global.Promise = bluebird;

global.Promise.config({ cancellation: true });

import {
  Router,
  Route,
  IndexRoute,
  IndexRedirect,
  browserHistory
} from 'react-router';

import Main from './components/main';
import Wines from './components/wines';
import Referrals from './components/referrals';
import Profile from './components/profile';
import Authenticate from './components/authenticate';
import ForgotPassword from './components/forgot-password';
import PasswordReset from './components/password-reset';
import PasswordResetSent from './components/password-reset-sent';
import AccountSetup from './components/account-setup';
import NotFound from './components/not-found';
import Impersonate from './components/impersonate';

import { getServiceContainer } from './services/service-container';

import { Provider } from 'unstated';

import { LoadingContainer } from './components/containers/loading-container';
import { ErrorContainer } from './components/containers/error-container';
import { ProfileContainer } from './components/containers/profile-container';
import { SalesHistoryContainer } from './components/containers/sales-history-container';
import { WineHistoryContainer } from './components/containers/wine-history-container';
import { SetupContainer } from './components/containers/setup-container';

import { createHttpProvider } from './lib/http-provider';
import { PasswordUpdate } from './components/password-update';
import { ProfileUpdate } from './components/profile-update';
import { ProfileCreate } from './components/profile-create';
import { PaymentMethod } from './components/payment-method';
import { PaymentMethodUpdate } from './components/payment-method-update';
import { OrderHistory } from './components/order-history';
import { OrderDetail } from './components/order-detail';
import { ConveyancePreferences } from './components/conveyance-preferences';
import { Toaster } from 'react-hot-toast';

import * as cache from '@thi.ng/cache';

import store from 'store';

import * as cookie from 'react-cookies';

import * as _ from 'lodash';

import navigation from './lib/navigation-utilities';

const { brixEndpoint, apiEndpoint } = window.__PRELOADED_STATE__.meta;

const logoutOnFailure = (status) => status === 401 && profileContainer.logOut();

const brixTransport = createHttpProvider(brixEndpoint, logoutOnFailure);

const apiTransport = createHttpProvider(apiEndpoint, logoutOnFailure, false);

const winesCacheImplementation = new cache.TLRUCache(null, {
  maxsize: 100,
  ttl: 1800000
});

const serviceContainer = getServiceContainer(
  brixTransport,
  apiTransport,
  store,
  winesCacheImplementation,
  cookie
);

const loadingContainer = new LoadingContainer(serviceContainer);
const profileContainer = new ProfileContainer(serviceContainer);
const saleHistoryContainer = new SalesHistoryContainer(serviceContainer);
const wineHistoryContainer = new WineHistoryContainer(serviceContainer);
const errorContainer = new ErrorContainer(serviceContainer);
const setupContainer = new SetupContainer(serviceContainer);

const assertKnownIdentity = (params) => {
  if (profileContainer.isIdentified()) return true;

  navigation.replace('/login');

  return false;
};

const assertLoggedIn = (params) => {
  if (profileContainer.isAuthenticated()) return true;

  navigation.replace('/login');

  return false;
};

const mergeMiddleware =
  (...middlewareStack) =>
  (params) =>
    _.every(middlewareStack, (middleware) => middleware(params));

const checkPrimaryProfileLevel = () => {
  if (profileContainer.isCurrentProfilePrimary()) return true;

  navigation.replace('/account');

  return false;
};

const redirectToAccountIfIdentified = () => {
  if (profileContainer.isIdentified()) {
    navigation.replace('/account');
    return false;
  }
  return true;
};

const onUpdate = () => {
  window.scrollTo(0, 0);
};

const App = profileContainer.refreshDetails().then(() => () => {
  return (
    <Provider
      inject={[
        loadingContainer,
        profileContainer,
        saleHistoryContainer,
        wineHistoryContainer,
        errorContainer,
        setupContainer
      ]}
    >
      <Router history={browserHistory} onUpdate={onUpdate}>
        <Route
          exact
          path="/account"
          component={Main}
          onEnter={assertKnownIdentity}
        >
          <IndexRedirect to="/account/wines" />
          <Route
            exact
            path="wines"
            component={Wines}
            onEnter={assertKnownIdentity}
          />
          <Route
            exact
            path="referrals"
            component={Referrals}
            onEnter={assertKnownIdentity}
          />

          <Route exact path="profile" component={Profile}>
            <IndexRoute
              component={ProfileUpdate}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
            <Route
              exact
              path="profile_update"
              component={ProfileUpdate}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
            <Route
              exact
              path="profile_create"
              component={ProfileCreate}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
            <Route
              exact
              path="conveyance"
              component={ConveyancePreferences}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
            <Route
              exact
              path="password_update"
              component={PasswordUpdate}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
            <Route
              exact
              path="payment_method"
              component={PaymentMethod}
              onEnter={mergeMiddleware(
                assertKnownIdentity,
                assertLoggedIn,
                checkPrimaryProfileLevel
              )}
            />
            <Route
              exact
              path="payment_method_update"
              component={PaymentMethodUpdate}
              onEnter={mergeMiddleware(
                assertKnownIdentity,
                assertLoggedIn,
                checkPrimaryProfileLevel
              )}
            />
            <Route
              exact
              path="orders/:orderId"
              component={OrderDetail}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
            <Route
              exact
              path="orders"
              component={OrderHistory}
              onEnter={mergeMiddleware(assertKnownIdentity, assertLoggedIn)}
            />
          </Route>
        </Route>
        <Route
          path="/setup/:setupToken"
          component={AccountSetup}
          onEnter={redirectToAccountIfIdentified}
        />
        <Route path="/impersonate" component={Impersonate} />
        <Route
          exact
          path="/"
          component={Authenticate}
          onEnter={redirectToAccountIfIdentified}
        />
        <Route exact path="/login" component={Authenticate} />
        <Route path="/forgot_password" component={ForgotPassword} />
        <Route path="/password_reset/:resetToken" component={PasswordReset} />
        <Route path="/password_reset" component={PasswordResetSent} />
        <Route path="*" exact={true} component={NotFound} />
      </Router>
      <Toaster position="bottom-center" />
    </Provider>
  );
});

export default App;
